import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { UnitType } from '@vk-hr-tek/core/units';

import { Collection, EventType as CatalogEventType } from '@app/gen/event-type';
import {
  CompanyProfilesSettings,
  EditableCompany,
  UnitItem,
  UnitTypeItem,
  UpdateCompanyProfilesSettings,
} from '@app/gen/settings';

import { UpdateCompanyProfilesSettingsDto } from '../../dto';
import { Company, EventType } from '../../types';
import { COLLECTION_ICONS, EVENT_ROLES, PRIORITIES } from '../../constants';

@injectable()
export class SettingsCompaniesMapper {
  constructor(private format: FormatService) {}

  processCompanies(companies: EditableCompany[]): Company[] {
    return companies.map((company) => ({
      id: company.company_item.id,
      name: company.company_item.name,
      accountName: company.account_name,
      licenseTotal: company.employee_limit,
      licenseUsed: company.employee_count,
      signType: company.sign_type,
      users: company.user_with_sign_type,
      importOperationalUnits: company.import_operational_units,
      showOperationalManager: company.show_operational_manager,
      showLegalManager: company.show_legal_manager,
      showFunctionalManager: company.show_functional_manager,
      allowFunctional: company.allow_functional,
      importLegalUnits: company.import_legal_units,
      payslipsEnabled: company.payslips_enabled,
      updateManagersInEvents: company.update_managers_in_events,
      employeeSignTypeSource: company.employee_sign_type_source,
      availableSignTypes: company.available_sign_types,
      updateManagerStartsParentUnit: company.manager_starts_from_parent_unit,
      earlyEmployeeEventAccessEnabled:
        company.early_employee_event_access_enabled,
      separateUnit: company.separate_units_enabled,
      roleModelEnabled: company.role_model_enabled,
      pdfDownloadAccessEnabled: company.employee_download_pdf_access,
      showClosestTaskWidget: company.show_closest_task_widget,
      businessTripsEnabled: company.business_trips_enabled,
      accountRoleModelExcluded: company.account_role_excluded,
      accountRoleModelEnabled: company.account_role_model_enabled,
      timezoneId: company.timezone_id,
      timezoneName: company.timezone_name,
      exceptionsTimezoneCount: company.exceptions_timezone_count,
      importTimezones: company.import_timezones,
      importTimezonesAvailable: company.import_timezones_available,
      managerSettingApprove: company.manager_setting_approve,
      accountWithOneCompany: company.account_with_one_company,
      accountId: company.account_id,
      isNotSynced: company.skip_account_sync || [],
      showProcessCatalog: !!company.process_catalog_available,
      searchUsersEnabled: !!company.search_users_enabled,
      eventsEnabled: this.isEventsEnabled(company),
    }));
  }

  private isEventsEnabled(company: EditableCompany): boolean {
    return [
      company.early_employee_event_access_enabled,
      company.update_managers_in_events,
      company.manager_starts_from_parent_unit,
      company.manager_setting_approve,
      company.process_catalog_available,
    ].every((field) => field !== undefined);
  }

  processCompanyProfiles(
    data: CompanyProfilesSettings,
  ): UpdateCompanyProfilesSettingsDto {
    return {
      secureProfiles: data.enabled ? 'true' : 'false',
      ...(data.source && { accessProfilesSource: data.source }),
      ...(data.unit_type && { unitType: data.unit_type }),
      ...(data.units && { unitIds: data.units.map(({ id }) => id) }),
      ...(data.include_separate_units && {
        separateUnitsEnabled: data.include_separate_units,
      }),
      ...(data.vacation_schedule_profiles !== undefined && {
        vacationScheduleProfiles: data.vacation_schedule_profiles
          ? 'true'
          : 'false',
      }),
    };
  }

  processUnitTypesSorting(unitTypes: UnitTypeItem[]) {
    if (!unitTypes.length) return [];

    return [
      ...unitTypes.filter(({ type }) => type === 'legal'),
      ...unitTypes.filter(({ type }) => type !== 'legal'),
    ];
  }

  processSeparateUnits(separateUnits?: UnitItem[], unitType?: UnitType) {
    if (!separateUnits || !unitType) return [];

    const sortedSeparateUnits = separateUnits.reduce<
      Record<UnitType, { label: string; value: string }[]>
    >(
      (acc, { id, name, type }) => {
        if (!acc[type]) acc[type] = [];

        acc[type].push({ label: name, value: id });

        return acc;
      },
      {
        legal: [],
        operational: [],
      },
    );

    return sortedSeparateUnits[unitType];
  }

  processUpdateCompanyProfilesSettings(
    profilesSettings: UpdateCompanyProfilesSettingsDto,
  ): UpdateCompanyProfilesSettings {
    const isSecureProfilesEnabled = this.format.toBoolean(
      profilesSettings.secureProfiles,
    );

    if (!isSecureProfilesEnabled) {
      return {
        enabled: false,
      };
    }

    const tempProfilesSettings: UpdateCompanyProfilesSettings = {
      enabled: true,
      source: profilesSettings.accessProfilesSource,
    };

    if (profilesSettings.representativesRelations !== undefined) {
      tempProfilesSettings.reset_default_profile = this.format.toBoolean(
        profilesSettings.representativesRelations,
      );
    }

    if (profilesSettings.vacationScheduleProfiles) {
      tempProfilesSettings.vacation_schedule_profiles = this.format.toBoolean(
        profilesSettings.vacationScheduleProfiles,
      );
    }

    if (tempProfilesSettings.source === 'import') {
      return tempProfilesSettings;
    }

    const tempUnits = profilesSettings.unitIds?.filter(Boolean);
    if (!!tempUnits?.length) {
      tempProfilesSettings.units = tempUnits;
    }

    return {
      ...tempProfilesSettings,
      unit_type: profilesSettings.unitType,
      include_separate_units: profilesSettings.separateUnitsEnabled,
    };
  }

  processCompanyCatalogCollections(collections: Collection[]) {
    return collections.map(({ event_types_count, type, ...collection }) => ({
      ...collection,
      count: event_types_count || 0,
      icon: COLLECTION_ICONS[type],
    }));
  }

  processEventTypesList(data: CatalogEventType[]) {
    const initialValues: Record<string, EventType> = {};
    const eventTypes = data.map(({ priority, start_side, ...eventType }) => {
      initialValues[eventType.id] = {
        ...eventType,
        priority: priority || 'none',
        sides: start_side,
      };

      return {
        ...eventType,
        priority: priority && PRIORITIES[priority],
        sides: start_side.map((side) => ({
          label: EVENT_ROLES[side].label,
          color: EVENT_ROLES[side].color,
        })),
      };
    });
    return { initialValues, eventTypes };
  }
}
